import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Store, TreePine, Bus, MapPin } from "lucide-react";
import { api, assetUrl } from "../lib/api";

const A = "/api/assets/";
const IMG = {
  hero: A + "b19a9d8d-cae6-48c8-b4ba-41994f78ebd2",
  plaza: A + "c2c18e56-6af4-4f64-bb27-d2bc769ddda9",
  pond: A + "49f3b340-be85-4926-9a66-75af5c999b29",
  dillon: A + "4807b526-82a0-474e-9736-1dd65b37e4de",
  trail: A + "f7386091-dd20-433c-b7b4-92403bc98030",
};

const Community = () => {
  const [biz, setBiz] = useState([]);
  const [places, setPlaces] = useState([]);
  const [transit, setTransit] = useState([]);

  useEffect(() => {
    api.get("/community/businesses").then((r) => setBiz(r.data.items));
    api.get("/community/places").then((r) => setPlaces(r.data.items));
    api.get("/community/transit").then((r) => setTransit(r.data.items));
  }, []);

  const blocks = [
    {
      to: "/community/local-businesses", img: IMG.plaza, icon: Store,
      title: "Local Businesses", desc: "Shops, services and independent specialists in and around Deer Park.",
      preview: biz.slice(0, 4).map((b) => b.name),
    },
    {
      to: "/community/parks-and-amenities", img: IMG.dillon, icon: TreePine,
      title: "Parks & Everyday Life", desc: "Parks, ponds, trails, playgrounds, childcare and schools.",
      preview: places.slice(0, 4).map((p) => p.name),
    },
    {
      to: "/community/getting-around", img: IMG.trail, icon: Bus,
      title: "Getting Around", desc: "Bus routes, stops, and walking and road connections.",
      preview: transit.slice(0, 3).map((t) => t.name),
    },
  ];

  return (
    <div className="bg-[#12201A] min-h-screen" data-testid="community-page">
      {/* hero */}
      <section className="relative h-[60vh] min-h-[420px] w-full overflow-hidden">
        <img src={assetUrl(IMG.hero)} alt="Deer Park Estates community" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0C1510]/70 via-[#0C1510]/45 to-[#12201A]" />
        <div className="relative z-10 h-full max-w-[1200px] mx-auto px-5 md:px-8 flex flex-col justify-end pb-12">
          <p className="eyebrow text-[#C9B79C] mb-3">Community</p>
          <h1 className="font-display font-light text-4xl md:text-6xl text-[#F3EEE3] leading-[1.03] max-w-3xl">Life around Deer Park Estates</h1>
          <p className="text-[#F3EEE3]/75 mt-4 max-w-2xl leading-relaxed font-light">
            A master-planned community in northwest Spruce Grove — built around two ponds, preserved natural areas and
            tree-lined trails, with a plaza of everyday shops, services and transit right at the entrance.
          </p>
        </div>
      </section>

      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16">
        {/* three big navigation blocks */}
        <div className="grid md:grid-cols-3 gap-5" data-testid="community-blocks">
          {blocks.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.to} className="rounded-3xl overflow-hidden border border-[#F3EEE3]/12 bg-[#0F1B15] flex flex-col">
                <Link to={b.to} className="group block aspect-[16/10] overflow-hidden relative">
                  <img src={assetUrl(b.img)} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F1B15] via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-[#12201A]/70 backdrop-blur flex items-center justify-center text-[#C9B79C]"><Icon size={20} /></span>
                </Link>
                <div className="p-6 flex flex-col flex-1">
                  <h2 className="font-display text-xl text-[#F3EEE3]">{b.title}</h2>
                  <p className="text-[#F3EEE3]/60 text-sm mt-2 leading-relaxed">{b.desc}</p>
                  {b.preview.length > 0 && (
                    <ul className="mt-4 space-y-1.5 flex-1">
                      {b.preview.map((name, i) => (
                        <li key={i} className="text-[#F3EEE3]/70 text-sm flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#C9B79C]" /> {name}</li>
                      ))}
                    </ul>
                  )}
                  <Link to={b.to} data-testid={`explore-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="inline-flex items-center gap-1.5 text-[#C9B79C] text-sm mt-5 group hover:gap-2.5 transition-all">
                    Explore more <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* location CTA */}
        <section className="mt-14 rounded-3xl border border-[#C9B79C]/25 bg-[#0F1B15] p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-xl">
            <p className="eyebrow text-[#C9B79C] mb-2 flex items-center gap-2"><MapPin size={13} /> Location</p>
            <h3 className="font-display font-light text-2xl md:text-3xl text-[#F3EEE3] leading-tight">See where everything sits on the map</h3>
            <p className="text-[#F3EEE3]/65 mt-3">The plaza, ponds, parks and transit stops around Deer Park Estates.</p>
          </div>
          <Link to="/location" className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-8 py-4 rounded-full font-medium hover:bg-white transition-colors">
            Open the area map <ArrowRight size={16} />
          </Link>
        </section>

        {/* stories placeholder (kept out of nav until content exists) */}
      </div>
    </div>
  );
};

export default Community;
